<template>
  <div
    v-if="shouldRender"
    aria-atomic="false"
    aria-live="polite"
    :class="stackClass"
    :data-test="stackTestId"
  >
    <v-snackbar-queue
      ref="snackbarQueue"
      v-model="queuedSnackbarItems"
      closable
      data-test="snack-bar-item"
      display-strategy="overflow"
      :gap="12"
      :location="snackbarLocation"
      :rounded="props.mode === 'kiosk' ? 'xl' : 'lg'"
      :total-visible="3"
      :width="snackbarWidth"
    >
      <template #text="{ item }">
        <div v-if="props.mode === 'kiosk'" class="snack-bar__kiosk-content">
          <div
            class="snack-bar__kiosk-media"
            :class="{ 'snack-bar__kiosk-media--error': item.color === 'error' }"
            data-test="kiosk-toast-media"
          >
            <img
              v-if="item.color === 'success' && item.image"
              class="snack-bar__kiosk-image"
              :alt="item.imageAlt || item.title || item.text"
              :src="item.image"
            />
            <v-icon
              v-else-if="item.color === 'error'"
              class="snack-bar__kiosk-icon"
              data-test="kiosk-toast-error-icon"
              :icon="item.icon"
            />
            <v-icon v-else-if="item.icon" class="snack-bar__kiosk-icon" :icon="item.icon" />
          </div>
          <div class="snack-bar__kiosk-text">
            <div class="snack-bar__kiosk-title" data-test="kiosk-toast-title">
              {{ item.title || item.text }}
            </div>
            <div
              v-if="item.subtitle"
              class="snack-bar__kiosk-subtitle"
              data-test="kiosk-toast-subtitle"
            >
              {{ item.subtitle }}
            </div>
          </div>
        </div>
        <div v-else class="snack-bar__default-content">
          <v-icon v-if="item.icon" class="snack-bar__default-icon" :icon="item.icon" />
          <span class="snack-bar__message">{{ item.text }}</span>
        </div>
      </template>

      <template #actions="{ item, props: actionProps }">
        <v-btn
          class="snack-bar__dismiss"
          :aria-label="getDismissLabel(item.text)"
          icon="mdi-close"
          :size="props.mode === 'kiosk' ? 'large' : 'small'"
          variant="text"
          @click="actionProps.onClick"
        />
      </template>
    </v-snackbar-queue>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import type { SnackBarToast, SnackBarToastPresentation } from "@/shared/types/global";
import i18n from "@/app/plugins/i18n";

interface SnackBarQueuePresentation {
  text: string;
  color: string;
  timeout: number;
  icon: string;
  title?: string;
  subtitle?: string;
  image?: string | null;
  imageAlt?: string;
  contentProps: {
    class?: string[];
    "data-timeout": number;
    "data-width": number;
    role: "alert" | "status";
  };
}

interface SnackBarQueueItem extends SnackBarQueuePresentation {
  id: number;
  promise?: Promise<unknown>;
  success?: (value: unknown) => SnackBarQueuePresentation;
  error?: (error: unknown) => SnackBarQueuePresentation;
  onDismiss: () => void;
}

const props = withDefaults(
  defineProps<{
    mode?: "default" | "kiosk";
  }>(),
  {
    mode: "default",
  },
);

const route = useRoute();
const snackBarStore = useSnackBarStore();
const { toasts } = storeToRefs(snackBarStore);
const snackbarQueue = ref();
const queuedSnackbarItems = ref<SnackBarQueueItem[]>([]);
const queuedToastIds = new Set<number>();

const isKioskRoute = computed(() => {
  const routeName = typeof route.name === "string" ? route.name : "";
  const routePath = "path" in route && typeof route.path === "string" ? route.path : "";

  return routeName.startsWith("kiosk") || routePath.startsWith("/kiosk/");
});

const shouldRender = computed(() =>
  props.mode === "kiosk" ? isKioskRoute.value : !isKioskRoute.value,
);

const visibleToasts = computed(() => {
  if (props.mode === "kiosk") {
    return toasts.value.filter((toast) => toast.mode === "kiosk");
  }

  return toasts.value.filter((toast) => toast.mode === "default");
});

const snackbarWidth = computed(() => (props.mode === "kiosk" ? 640 : 420));

const createQueuePresentation = (
  toast: SnackBarToastPresentation,
  mode: "default" | "kiosk",
): SnackBarQueuePresentation => {
  const timeout = toast.timeout ?? snackBarStore.defaultTimeout;

  return {
    text: toast.message,
    color: toast.color,
    timeout,
    icon: toast.icon,
    title: toast.title,
    subtitle: toast.subtitle,
    image: toast.image,
    imageAlt: toast.imageAlt,
    contentProps: {
      class: ["snack-bar__item", ...(mode === "kiosk" ? ["snack-bar__item--kiosk"] : [])],
      "data-timeout": timeout,
      "data-width": mode === "kiosk" ? 640 : 420,
      role: toast.color === "error" ? "alert" : "status",
    },
  };
};

const createQueueItem = (toast: SnackBarToast): SnackBarQueueItem => ({
  id: toast.id,
  ...createQueuePresentation(toast, toast.mode),
  promise: toast.promise,
  success: toast.success
    ? (value) => createQueuePresentation(toast.success!(value), toast.mode)
    : undefined,
  error: toast.error
    ? (error) => createQueuePresentation(toast.error!(error), toast.mode)
    : undefined,
  onDismiss: () => snackBarStore.dismissToast(toast.id),
});

watch(
  [visibleToasts, shouldRender],
  ([nextToasts, canRender]) => {
    if (!canRender) {
      queuedSnackbarItems.value = [];
      queuedToastIds.clear();
      return;
    }

    const nextToastIds = new Set(nextToasts.map((toast) => toast.id));

    queuedSnackbarItems.value = queuedSnackbarItems.value.filter((item) =>
      nextToastIds.has(item.id),
    );

    for (const queuedToastId of queuedToastIds) {
      if (!nextToastIds.has(queuedToastId)) {
        queuedToastIds.delete(queuedToastId);
      }
    }

    nextToasts.forEach((toast) => {
      if (queuedToastIds.has(toast.id)) {
        return;
      }

      queuedToastIds.add(toast.id);
      queuedSnackbarItems.value.push(createQueueItem(toast));
    });
  },
  { immediate: true },
);

const getDismissLabel = (message: string): string =>
  i18n.global.t("common.snackbar.dismiss", { message });

const snackbarLocation = computed(() => (props.mode === "kiosk" ? "bottom center" : "top right"));

const stackClass = computed(() =>
  props.mode === "kiosk" ? "snack-bar__stack--kiosk" : "snack-bar__stack--default",
);

const stackTestId = computed(() =>
  props.mode === "kiosk" ? "kiosk-toast-stack" : "global-toast-stack",
);
</script>

<style scoped>
:deep(.snack-bar__item) {
  max-width: calc(100vw - 48px);
  min-width: 0;
}

:deep(.snack-bar__item--kiosk) {
  min-height: 96px;
  padding: 20px 18px 20px 24px;
}

.snack-bar__default-content {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 0;
}

.snack-bar__message {
  overflow-wrap: anywhere;
  white-space: normal;
}

.snack-bar__kiosk-content {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 18px;
  min-height: 76px;
  min-width: 0;
}

.snack-bar__kiosk-media {
  align-items: center;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  display: flex;
  flex: 0 0 72px;
  height: 72px;
  justify-content: center;
  overflow: hidden;
  width: 72px;
}

.snack-bar__kiosk-media--error {
  background: rgba(255, 255, 255, 0.12);
}

.snack-bar__kiosk-image {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.snack-bar__kiosk-icon {
  font-size: 42px;
}

.snack-bar__kiosk-text {
  min-width: 0;
}

.snack-bar__kiosk-title {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.snack-bar__kiosk-subtitle {
  font-size: 18px;
  line-height: 1.35;
  margin-top: 6px;
  opacity: 0.9;
  overflow-wrap: anywhere;
}
</style>
